// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Platform-agnostic allow-list of integrations that surface the
 * drift-resolution popover. IntegrationStatus consults this to decide
 * whether to wire a chip's click to the popover or fall back to the
 * default external-link behavior.
 */
import { Integration, IntegrationId } from '@/utilities/integration';

/** Hardfin entity types that support drift resolution today. */
export type DriftEntityType = 'location' | 'company' | 'project';

/**
 * Integrations whose chip opens a drift-resolution popover. Add a
 * platform here when its BE endpoints come online — IntegrationStatus
 * will mount the dispatch and the chip will surface the needs-review
 * dot automatically.
 */
const DRIFT_INTEGRATION_IDS: ReadonlySet<IntegrationId> =
  new Set<IntegrationId>(['SFDC']);

// True when the organization has at least one drift-detection CRM enabled.
// Used to gate the sync-status list filter, which is meaningless without a
// drift-capable integration connected. Unlike isDriftCapable this is an
// org-level check on enabled integrations and does not require a per-entity
// link to be populated.
export function hasDriftDetectionIntegration(
  integrations: readonly Integration[]
): boolean {
  return integrations.some((integration) =>
    DRIFT_INTEGRATION_IDS.has(integration.id)
  );
}

// Drift-capable means the integration platform supports the resolution
// popover AND we have a real external link (link populated and not the
// `#error_msg` placeholder used for error-state chips).
export function findDriftCapableIntegration(
  integrations: readonly Integration[]
): Integration | undefined {
  return integrations.find(isDriftCapable);
}

// Predicate form of the drift-capable gate so callers can reuse the same
// check on a known integration entry (e.g. the chip already has the SFDC
// entry in hand and just needs to decide whether to surface drift state).
function isDriftCapable(integration: Integration): boolean {
  return (
    DRIFT_INTEGRATION_IDS.has(integration.id) &&
    !!integration.link &&
    !integration.link.startsWith('#')
  );
}

// True only when the integration is drift-capable AND its needsReview flag
// is set. Unlinked or error-state chips have needsReview suppressed because
// they can't be acted on — the amber indicator would be misleading.
export function isDriftCapableNeedsReview(integration: Integration): boolean {
  return integration.needsReview && isDriftCapable(integration);
}
